import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { apiFetch } from '@/services/apiGateway';
import { useAuthStore } from '@/store/authStore';
import { useData } from '@/hooks/useData';
import { useNetworkStatus } from '@/hooks/useNetworkStatus';
import { useTranslation } from 'react-i18next';
import { AiMarkdownRenderer } from '@/components/ui/AiMarkdownRenderer';

interface Flashcard {
  question: string;
  answer: string;
}

interface FlashcardsResponse {
  flashcards: Flashcard[];
  subject: string;
  topic: string;
}

interface SessionResponse {
  session_id: string;
  streak_count: number;
}

interface TimetableEntry {
  subject: string;
  day: string;
  start_time: string;
  end_time: string;
  room: string | null;
}

interface TimetableResponse {
  entries: TimetableEntry[];
}

export function FlashcardSession() {
  const isOffline = useNetworkStatus();
  const { t } = useTranslation();
  const [cards, setCards]               = useState<Flashcard[]>([]);
  const [isLoading, setIsLoading]       = useState(true);
  const [error, setError]               = useState<string | null>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isFlipped, setIsFlipped]       = useState(false);
  const [sessionComplete, setSessionComplete] = useState(false);
  const [newStreak, setNewStreak]       = useState<number | null>(null);
  const [selectedSubject, setSelectedSubject] = useState('');
  const [studyTheme, setStudyTheme]           = useState('');

  // Track wall-clock time for an accurate duration_minutes in the session log
  const sessionStartRef = useRef<number>(Date.now());

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const subject = searchParams.get('subject') ?? 'Mathematics';

  const { childId, setStreakCount } = useAuthStore();

  // Fetch timetable so the completion screen can offer subject choices
  const { data: timetableData } = useData<TimetableResponse>(
    childId ? `/children/${childId}/timetable` : null,
  );
  const uniqueSubjects = Array.from(
    new Set((timetableData?.entries ?? []).map((e) => e.subject)),
  );

  // Fetch flashcards from the AI proxy
  useEffect(() => {
    if (isOffline) {
      setIsLoading(false);
      return;
    }
    sessionStartRef.current = Date.now();
    setIsLoading(true);
    setError(null);
    apiFetch<FlashcardsResponse>('/ai/flashcards', {
      method: 'POST',
      body: JSON.stringify({ subject, topic: subject, count: 8 }),
    })
      .then((res) => setCards(res.flashcards ?? []))
      .catch((err: unknown) => {
        const msg = err instanceof Error ? err.message : t('flashcard.generate_failed');
        setError(msg.includes('429') ? t('ai_session.limit_reached') : msg);
      })
      .finally(() => setIsLoading(false));
  }, [subject, isOffline, t]);

  // Log the completed session and refresh streak count
  useEffect(() => {
    if (!sessionComplete || !childId) return;
    const elapsedMin = Math.round((Date.now() - sessionStartRef.current) / 60_000);
    const duration_minutes = Math.max(5, Math.min(240, elapsedMin || 15));
    apiFetch<SessionResponse>(`/children/${childId}/sessions`, {
      method: 'POST',
      body: JSON.stringify({ subject, topic: subject, format: 'short', duration_minutes }),
    })
      .then((res) => {
        setNewStreak(res.streak_count);
        setStreakCount(res.streak_count);
      })
      .catch(() => { /* best-effort — session logging must not block the UI */ });
  }, [sessionComplete, childId, subject, setStreakCount]);

  const card = cards[currentIndex];

  const advance = () => {
    if (currentIndex + 1 >= cards.length) {
      setSessionComplete(true);
    } else {
      setCurrentIndex((i) => i + 1);
      setIsFlipped(false);
    }
  };

  /* ── Offline gate ── */
  if (isOffline && !sessionComplete && cards.length === 0) {
    return (
      <div className="page-center">
        <div className="phone-mockup" style={{ display: 'flex', flexDirection: 'column' }}>
          <div className="app-content" style={{ justifyContent: 'center', alignItems: 'center', textAlign: 'center' }}>
            <p style={{ fontSize: '2.5rem', margin: '0 0 16px' }}>📡</p>
            <h2 style={{ fontSize: '1.1rem', fontWeight: 700, color: 'var(--text-dark)', margin: '0 0 8px' }}>
              {t('ai_session.offline_title')}
            </h2>
            <button type="button" className="btn btn-outline" disabled style={{ width: '100%', marginTop: '16px', opacity: 0.5 }}>
              {t('ai_session.offline_cta')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  /* ── Session complete ── */
  if (sessionComplete) {
    const canContinue = selectedSubject.trim() !== '' && studyTheme.trim() !== '';
    const goQuiz = () => {
      if (!canContinue) return;
      navigate(`/student/quiz?subject=${encodeURIComponent(selectedSubject)}&theme=${encodeURIComponent(studyTheme)}`);
    };
    const goExplain = () => {
      if (!canContinue) return;
      navigate(`/student/explain?subject=${encodeURIComponent(selectedSubject)}&theme=${encodeURIComponent(studyTheme)}`);
    };
    const goFlashcards = () => {
      if (!canContinue) return;
      navigate(`/student/flashcards?subject=${encodeURIComponent(selectedSubject)}&theme=${encodeURIComponent(studyTheme)}`);
    };

    return (
      <div className="page-center">
        <div className="phone-mockup">
          <div className="app-content" style={{ gap: '0' }}>

            {/* Success header */}
            <div style={{ textAlign: 'center', padding: '24px 0 20px' }}>
              <div style={{ fontSize: '3.5rem' }}>🎉</div>
              <h1 style={{ fontSize: '1.6rem', fontWeight: 700, color: 'var(--text-dark)', margin: '8px 0 6px' }}>
                {t('session_complete.title')}
              </h1>
              <div className="streak-badge" style={{ display: 'inline-block', fontSize: '0.95rem', padding: '6px 18px' }}>
                🔥 {newStreak !== null ? t('session_complete.streak', { count: newStreak }) : t('session_complete.streak_plus')}
              </div>
              <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem', margin: '10px 0 0' }}>
                {t('session_complete.great_work', { subject })}
              </p>
            </div>

            {/* Next steps card */}
            <div className="card" style={{ padding: '20px', textAlign: 'left', marginTop: '4px' }}>
              <h3 style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--text-dark)', margin: '0 0 16px' }}>
                {t('session_complete.momentum')}
              </h3>

              <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-muted)', marginBottom: '6px' }}>
                {t('session_complete.subject')}
              </label>
              <select
                value={selectedSubject}
                onChange={(e) => setSelectedSubject(e.target.value)}
                style={{
                  width: '100%', padding: '12px', borderRadius: '8px',
                  border: '1px solid var(--border)', marginBottom: '16px',
                  fontFamily: 'inherit', fontSize: '0.9rem', background: 'white',
                  color: selectedSubject ? 'var(--text-dark)' : 'var(--text-muted)',
                  boxSizing: 'border-box',
                }}
              >
                <option value="">{t('flashcard.choose_subject')}</option>
                {uniqueSubjects.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>

              <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-muted)', marginBottom: '6px' }}>
                {t('session_complete.theme')}
              </label>
              <input
                type="text"
                value={studyTheme}
                onChange={(e) => setStudyTheme(e.target.value)}
                placeholder={t('flashcard.theme_placeholder')}
                style={{
                  width: '100%', padding: '12px', borderRadius: '8px',
                  border: '1px solid var(--border)', marginBottom: '0',
                  fontFamily: 'inherit', fontSize: '0.9rem',
                  boxSizing: 'border-box',
                }}
              />

              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '16px' }}>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={goQuiz}
                  disabled={!canContinue}
                  style={{ width: '100%' }}
                >
                  📝 {t('session_complete.start_quiz')}
                </button>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <button
                    type="button"
                    className="btn btn-outline"
                    onClick={goExplain}
                    disabled={!canContinue}
                    style={{ flex: 1 }}
                  >
                    💬 {t('session_complete.explain_this')}
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline"
                    onClick={goFlashcards}
                    disabled={!canContinue}
                    style={{ flex: 1 }}
                  >
                    🎴 {t('session_complete.new_cards')}
                  </button>
                </div>
              </div>
            </div>

            {/* Back link */}
            <button
              type="button"
              onClick={() => navigate('/student')}
              style={{
                background: 'transparent', border: 'none', color: 'var(--text-muted)',
                fontSize: '0.85rem', fontFamily: 'inherit', cursor: 'pointer',
                padding: '16px 0 8px', alignSelf: 'center',
              }}
            >
              {t('session_complete.back_dash')}
            </button>

          </div>
        </div>
      </div>
    );
  }

  /* ── Active session ── */
  return (
    <div className="page-center">
      <div className="phone-mockup">

        {/* Top navigation bar — no BottomTabBar in study mode */}
        <div style={{
          background: 'white',
          borderBottom: '1px solid var(--border)',
          padding: '45px 20px 12px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexShrink: 0,
        }}>
          <button
            type="button"
            onClick={() => navigate('/student')}
            style={{
              background: 'transparent', border: 'none', color: 'var(--primary)',
              fontSize: '0.95rem', fontFamily: 'inherit', fontWeight: 600,
              cursor: 'pointer', padding: 0,
            }}
          >
            {t('explain.leave')}
          </button>
          <span style={{ fontWeight: 600, color: 'var(--text-dark)', fontSize: '0.9rem' }}>
            {subject}
          </span>
          <span style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>
            {cards.length > 0 ? `${currentIndex + 1} / ${cards.length}` : '—'}
          </span>
        </div>

        {/* Content area */}
        <div className="app-content" style={{ justifyContent: 'center', alignItems: 'center', gap: '24px' }}>

          {/* Loading: pulsing skeleton + descriptive text */}
          {isLoading && (
            <>
              <div
                className="loading-skeleton"
                style={{ height: '400px', width: '100%', borderRadius: '16px' }}
              />
              <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', margin: 0, textAlign: 'center' }}>
                {t('flashcard.generating_session')}
              </p>
            </>
          )}

          {/* Error */}
          {!isLoading && error && (
            <div style={{ textAlign: 'center' }}>
              <p style={{ color: 'var(--danger)', fontSize: '0.9rem', marginBottom: '16px' }}>{error}</p>
              <button type="button" className="btn btn-primary" onClick={() => navigate('/student')}>
                {t('ai_session.back_to_dashboard')}
              </button>
            </div>
          )}

          {/* Flip card */}
          {!isLoading && !error && cards.length > 0 && card && (
            <>
              <div className="flashcard-container">
                <div
                  className={`flashcard${isFlipped ? ' flipped' : ''}`}
                  onClick={() => setIsFlipped((f) => !f)}
                >
                  <div className="flashcard-face flashcard-front-face">
                    <p style={{
                      fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-muted)',
                      textTransform: 'uppercase', letterSpacing: '1px', margin: '0 0 16px',
                    }}>
                      {t('flashcard.question_label')}
                    </p>
                    <AiMarkdownRenderer content={card.question} />
                  </div>
                  <div className="flashcard-face flashcard-back-face">
                    <p style={{
                      fontSize: '0.75rem', fontWeight: 600, color: 'var(--primary)',
                      textTransform: 'uppercase', letterSpacing: '1px', margin: '0 0 16px',
                    }}>
                      {t('flashcard.answer_label')}
                    </p>
                    <AiMarkdownRenderer content={card.answer} />
                  </div>
                </div>
              </div>

              {!isFlipped ? (
                <button type="button" className="btn btn-primary" onClick={() => setIsFlipped(true)}>
                  {t('flashcard.tap_flip')}
                </button>
              ) : (
                <div style={{ display: 'flex', gap: '12px', width: '100%' }}>
                  <button
                    type="button"
                    onClick={advance}
                    style={{
                      flex: 1, padding: '12px',
                      border: '2px solid var(--success)', borderRadius: '8px',
                      background: '#F0FDF4', color: 'var(--success)',
                      fontFamily: 'inherit', fontWeight: 700, fontSize: '0.9rem', cursor: 'pointer',
                    }}
                  >
                    ✓ {t('flashcard.got_it')}
                  </button>
                  <button
                    type="button"
                    onClick={advance}
                    style={{
                      flex: 1, padding: '12px',
                      border: '2px solid var(--danger)', borderRadius: '8px',
                      background: '#FEF2F2', color: 'var(--danger)',
                      fontFamily: 'inherit', fontWeight: 700, fontSize: '0.9rem', cursor: 'pointer',
                    }}
                  >
                    ✗ {t('flashcard.need_practice')}
                  </button>
                </div>
              )}

              {/* Progress dots — green for completed, primary for current, gray for upcoming */}
              <div style={{ display: 'flex', gap: '6px' }}>
                {cards.map((_c, i) => (
                  <div
                    key={i}
                    style={{
                      width: '8px', height: '8px', borderRadius: '50%',
                      background: i < currentIndex
                        ? 'var(--success)'
                        : i === currentIndex
                          ? 'var(--primary)'
                          : 'var(--border)',
                      transition: 'background 0.2s',
                    }}
                  />
                ))}
              </div>
            </>
          )}

          {/* Empty state — Claude returned zero cards */}
          {!isLoading && !error && cards.length === 0 && (
            <div style={{ textAlign: 'center' }}>
              <p style={{ color: 'var(--text-muted)', marginBottom: '16px' }}>
                {t('flashcard.empty_state')}
              </p>
              <button type="button" className="btn btn-primary" onClick={() => navigate('/student')}>
                {t('ai_session.back_to_dashboard')}
              </button>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}
